import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { RecordData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { arrayMove, sortArrayBasedOnSubset, sortBy } from 'webapp/utils/common';
import {
	clearTableCellWidthsOnDragStop,
	getSortableContainment,
	getSortableItemIndex,
	setTableCellWidthsOnDragStart,
} from 'webapp/utils/html';

import GoalsBaseComponent, { type GoalsBaseArgs } from 'webapp/components/goals/base';

import type { DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import type FilterSortGoalInGrid from 'webapp/models-custom/filter-sort/goal-my-in-grid';
import type FilterSortGoalInList from 'webapp/models-custom/filter-sort/goal-my-in-list';
import type Goal from 'webapp/models-custom/goal';

export interface GoalsMyArgs extends GoalsBaseArgs {
	onAdd: () => void;
}

export default abstract class GoalsMyBaseComponent extends GoalsBaseComponent<GoalsMyArgs> {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;

	abstract filterSort: FilterSortGoalInGrid | FilterSortGoalInList;

	batchSelect: BatchSelect<Goal>;
	sortableElements: JQuery[] = [];

	constructor(owner: Owner, args: GoalsMyArgs) {
		super(owner, args);

		this.batchSelect = new BatchSelect<Goal>({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});
	}

	get sortedModel(): Goal[] {
		return sortBy(this.model, 'order', 'numericId');
	}

	get hasFavoriteGoals(): boolean {
		return this.sortedModel.filter((item) => item.favorite).length > 0;
	}

	get isFavoriteSectionVisible(): boolean {
		return this.favoriteGoalsFilterFn(this.filterSort.results).length > 0;
	}

	@action
	addSecondVisibleCheckContainerNode(node: HTMLElement) {
		this.addVisibleCheckContainerNode(node);
	}

	initSortable(
		ctEl: JQuery,
		itemSelectorString: string,
		placeholderElementFn: (el: JQuery) => JQuery,
		sortingFavorites = false,
	): void {
		const containment = getSortableContainment(ctEl);

		ctEl.sortable({
			handle: '.drag',
			containment,
			placeholder: {
				element: placeholderElementFn,
				update: function () {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: itemSelectorString,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				setTableCellWidthsOnDragStart(ui);

				const startIndex = getSortableItemIndex(ui.item, itemSelectorString);
				ui.item.find('.drag').attr('data-position', startIndex.toString());
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				clearTableCellWidthsOnDragStop(ui);

				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = getSortableItemIndex(ui.item, itemSelectorString),
					id = handle.attr('data-id');

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex, sortingFavorites);
					this.analyticsService.event(TrackingEvent.GOAL_REORDER, {
						goal_id: id,
						origin: 'goals',
					});
				}
			},
		});

		this.sortableElements.push(ctEl);
	}

	destroySortable(): void {
		this.sortableElements.forEach((el) => {
			if (el?.length && el.sortable('instance')) {
				el.sortable('destroy');
			}
		});
		this.sortableElements = [];
	}

	updateSortOrder(oldIndex: number, newIndex: number, sortingFavorites: boolean): void {
		if (!sortingFavorites) {
			const favoriteItemsCount = this.model.filter((item) => item.favorite).length;
			oldIndex += favoriteItemsCount;
			newIndex += favoriteItemsCount;
		}

		const orderedIds = this.sortedModel.map((m) => m.numericId),
			filterOrderedIds = arrayMove(
				this.filterSort.results.filter((item) => item.isOwnedByMe).map((m) => m.numericId),
				oldIndex,
				newIndex,
			),
			newSortOrder = sortArrayBasedOnSubset(orderedIds, filterOrderedIds);

		this.requestService
			.api<RecordData.Payload>('POST', '/goals/order', newSortOrder)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		this.args.model.forEach((item) => {
			item.order = newSortOrder.indexOf(item.numericId);
			item.applyBufferedChanges(['order']); // we won't be sending save
		});
	}

	@action
	teardownView(_node: HTMLElement): void {
		this.destroySortable();
	}
}
